/**
 * 表单控件: Input / Textarea / Select (原生 select 加样式)。
 * invalid=true 时红框 + aria-invalid。手机上字号 16px, 避免 iOS 聚焦时自动放大。
 */
import { forwardRef, type InputHTMLAttributes, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

export const controlClass = cn(
  "w-full rounded-lg border border-input bg-card text-foreground shadow-sm",
  "text-base md:text-sm placeholder:text-placeholder",
  "transition-colors outline-none",
  "focus-visible:border-brand focus-visible:ring-2 focus-visible:ring-ring/25",
  "disabled:cursor-not-allowed disabled:bg-muted disabled:opacity-70",
  "aria-[invalid=true]:border-danger aria-[invalid=true]:focus-visible:ring-danger/25",
);

interface InvalidProp {
  /** 校验失败: 红框 + aria-invalid (Field 传了 error 时会自动设置) */
  invalid?: boolean;
}

export type InputProps = InputHTMLAttributes<HTMLInputElement> & InvalidProp;

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, invalid, type = "text", ...props },
  ref,
) {
  return (
    <input
      ref={ref}
      type={type}
      aria-invalid={invalid || props["aria-invalid"] || undefined}
      className={cn(controlClass, "h-9 px-3", className)}
      {...props}
    />
  );
});

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & InvalidProp;

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, invalid, rows = 3, ...props },
  ref,
) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      aria-invalid={invalid || props["aria-invalid"] || undefined}
      className={cn(controlClass, "px-3 py-2 leading-relaxed", className)}
      {...props}
    />
  );
});

export type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & InvalidProp;

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { className, invalid, children, ...props },
  ref,
) {
  return (
    <div className={cn("relative", className)}>
      <select
        ref={ref}
        aria-invalid={invalid || props["aria-invalid"] || undefined}
        className={cn(controlClass, "h-9 appearance-none pl-3 pr-9")}
        {...props}
      >
        {children}
      </select>
      <ChevronDown
        aria-hidden
        className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
      />
    </div>
  );
});
